import React, { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import FullPageLoader from '../components/FullPageLoader';

// Components
const AuthLayout = lazy(() => import('../components/layout/AuthLayout'));
const Dashboard = lazy(() => import('../views/Dashboard'));
const Login = lazy(() => import('../views/Login'));
const NoPage = lazy(() => import('../views/404'));
const ForgotPassword = lazy(() => import('../views/ForgotPassword'));
const ResetPassword = lazy(() => import('../views/ResetPassword'));
const SuggestionBox = lazy(() => import('../views/SuggestionBox'));
const Questions = lazy(() => import('../views/Questions'));
const Answers = lazy(() => import('../views/Answers'));
const Employees = lazy(() => import('../views/Employees'));
const Tags = lazy(() => import('../views/Tags'));
const TagDetails = lazy(() => import('../views/Tags/TagDetails'));
const Profile = lazy(() => import('../views/Profile'));
const EditProfile = lazy(() => import('../views/Profile/EditProfile'));
const EditCompany = lazy(() => import('../views/Profile/EditCompany'));
const ChangePassword = lazy(() => import('../views/Profile/ChangePassword'));
const SSOView = lazy(() => import('../views/SSO'));
const NudgeDashboard = lazy(() => import('../views/Nudge'));
const Loops = lazy(() => import('../views/Loops'));
const SurveySchema = lazy(() => import('../views/SurveySchema'));
const Payment = lazy(() => import('../views/Payment'));
const SurveySchemaDetail = lazy(() =>
  import('../views/SurveySchema/SurveySchemaDetail'),
);
const WhatsappAnalytics = lazy(() => import('../views/WhatsappAnalytics'));
const ParentOutlet = lazy(() => import('../views/ParentOutlet'));
const Journey = lazy(() => import('../views/Journey'));
const JourneyDetail = lazy(() => import('../views/Journey/View'));
const Events = lazy(() => import('../views/Events'));
const EventsDashboard = lazy(() => import('../views/Events/EventsDashboard'));

// utils
import { useSession } from '../Providers/SessionProviders';

const OpportuneRoutes = () => {
  const { currentUser } = useSession();

  if (!currentUser) {
    return (
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route path="/:company" element={<ParentOutlet />}>
            <Route path="login" element={<Login />} />
            <Route path="sso/:token" element={<SSOView />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password/:token" element={<ResetPassword />} />
            <Route path="set-password/:token" element={<ResetPassword />} />
            <Route path="*" element={<NoPage />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  const allRoutes = [
    <Route
      key="view_feedbackconversation"
      path="suggestion-box"
      element={<SuggestionBox />}
    />,
    <Route key="view_question" path="questions" element={<Questions />} />,
    <Route key="view_answer" path="answers" element={<Answers />} />,
    <Route key="view_useraccount" path="employees" element={<Employees />} />,
    <Route key="view_tag" path="tags" element={<Tags />} />,
    <Route key="view_tag" path="tags/view/:tagId" element={<TagDetails />} />,
    <Route
      key="view_nudge"
      path="resolution-dashboard"
      element={<NudgeDashboard />}
    />,
    <Route
      key="view_survey_schema"
      path="survey-schema"
      element={<SurveySchema />}
    />,
    <Route key="view_loops" path="loop" element={<Loops />} />,
    <Route key="view_payment" path="payment" element={<Payment />} />,
    <Route
      key="view_survey_schema_dashboard"
      path="survey-schema/:surveyId/dashboard"
      element={<SurveySchemaDetail />}
    />,
    <Route
      key="view_whatsapp_analytics"
      path="whatsapp-analytics"
      element={<WhatsappAnalytics />}
    />,
    <Route key="view_journey" path="journey" element={<Journey />} />,
    <Route
      key="view_detail_journey"
      path="journey/view/:journeyId"
      element={<JourneyDetail />}
    />,
    <Route key="view_events" path="events" element={<Events />} />,
    <Route
      key="view_event_dashboard"
      path="events/:surveyId/dashboard"
      element={<EventsDashboard />}
    />,
  ];

  return (
    <AuthLayout>
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route path="/:company" element={<ParentOutlet />}>
            <Route path="" element={<Dashboard />} />
            <Route path="profile" element={<Profile />}>
              <Route path="edit" element={<EditProfile />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="company" element={<EditCompany />} />
            </Route>
            {allRoutes}
            <Route path="*" element={<NoPage />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthLayout>
  );
};

export default OpportuneRoutes;
